// @ts-ignore - Settings is not found?!
import tinymce, {Editor, Settings} from 'tinymce/tinymce';
import 'tinymce/themes/silver/theme';
import 'tinymce/icons/default/icons';
import 'tinymce/models/dom/model';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/code';
import 'tinymce/plugins/table';
import {global} from "../../appContext";
import {ObjectTypeId, TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../constants";
import {IBoundsAndRange, IRectCss, ITextRange} from "./index";
import AbstractText from "./AbstractText";
import {ISyncField} from "../../index";
import {nsDynamicVariable} from "./nsDynamicVariable";
import pubsub from "../../pubsub";
import getTextRange = nsDynamicVariable.getTextRange;
import text_area_richHtml from './text-area-rich.html?raw';

export let template = text_area_richHtml;

/** TinyMCE skin assets are served from /lib/tinymce (iframe-only; not bundled into app CSS). */
const TINYMCE_LIB = '/lib/tinymce';

export class viewModel extends AbstractText<HTMLTextAreaElement> {

    private static EDITOR_EVENTS = 'focusin click paste cut NodeChange resize input blur focusout';
    private static DOCUMENT_EVENTS = 'resize scroll underflowchanged overflowchanged';

    private editor!: Editor;
    private onKeyCaptureHandler!: (e: KeyboardEvent) => void;

    protected override onViewReady() {
        super.onViewReady();

        const settings: Settings = {
            convert_urls: false,
            relative_urls: false,
            remove_script_host: false,
            target: this.srcEl,
            branding: false,
            menu: null,
            menubar: "",
            // statusbar: false,
            selection_toolbar: "",
            toolbar: [ // @see: https://www.tinymce.com/docs/advanced/editor-control-identifiers/#toolbarcontrols
                'undo redo',
                'bold italic underline strikethrough',
                'alignleft aligncenter alignright alignjustify',
                'indent outdent blockquote',
                'bullist numlist',
                'link unlink',
                'removeformat',
                'image',
                'table',
                'code',
            ].join(' | '),
            plugins: ['lists', 'link', 'image', 'code', 'table'],
            skin_url: `${TINYMCE_LIB}/skins/ui/oxide`,
            content_css: `${TINYMCE_LIB}/skins/content/default/content.min.css`,
            valid_children: '+body[style]',
            setup: (editor: Editor) => {
                this.editor = editor;
                editor.on('init', () => {
                    this.editor.getBody().setAttribute('dynamicsupport', "true")
                    this.editor.getWin()?.frameElement?.setAttribute('dynamicsupport', "true");
                    this.editor.setContent(this.expression());
                    this.editor.getDoc().addEventListener("scroll", this.onEventHandler);
                    this.attachKeyCaptureListeners();
                    this.initListeners();
                });
            }
        };

        tinymce.init(settings);
    }

    private initListeners() {
        this.editor.on(viewModel.EDITOR_EVENTS, this.onEventHandler);
        viewModel.DOCUMENT_EVENTS.split(' ').forEach(name => document.addEventListener(name, this.onEventHandler));
    }

    /** Capture-phase keys on the iframe document run before TinyMCE bubble handlers. */
    private attachKeyCaptureListeners(): void {
        const doc = this.editor.getDoc();
        this.onKeyCaptureHandler = (e: KeyboardEvent) => {
            if (this.onKey(e) === false) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
            }
        };
        doc.addEventListener('keydown', this.onKeyCaptureHandler, true);
        doc.addEventListener('keyup', this.onKeyCaptureHandler, true);
    }

    private detachKeyCaptureListeners(): void {
        const doc = this.editor?.getDoc();
        if (doc && this.onKeyCaptureHandler) {
            doc.removeEventListener('keydown', this.onKeyCaptureHandler, true);
            doc.removeEventListener('keyup', this.onKeyCaptureHandler, true);
        }
    }

    private killListeners() {
        this.detachKeyCaptureListeners();
        this.editor?.off(viewModel.EDITOR_EVENTS, this.onEventHandler);
        this.editor?.getDoc().removeEventListener("scroll", this.onEventHandler);
        viewModel.DOCUMENT_EVENTS.split(' ').forEach(name => document.removeEventListener(name, this.onEventHandler));
    }

    protected override onKey(e: KeyboardEvent): boolean {
        switch (e.key) {
            case TEXT_WRAPPER_START:
                if (e.type == 'keydown') {
                    this.insertRawString(`${TEXT_WRAPPER_START}${TEXT_WRAPPER_END}`);
                    const selection = this.getDocument().getSelection();
                    const position = (selection?.focusOffset ?? 1) - 1;
                    this.setCaretPosition(position);
                    e.preventDefault();
                }
                break;
        }
        return super.onKey(e);
    }

    protected override onEvent(e: Event | null = null) {
        super.onEvent(e ?? undefined);
        if (e?.type == 'focusin') {
            pubsub.trigger('dynamic.editor.focusin', e)
        }
    }

    protected updateParamsExpression() {

        const innerHtml = this.editor.getBody()?.innerHTML ?? '';
        this.params.expression(innerHtml.replace(/&amp;/gm, (match: string, index: number, text: string) => {
            const textRangeExpression = nsDynamicVariable.getTextRange(index, text);
            if (!!textRangeExpression) {
                // do not use html entity
                return '&'
            } else {
                // do nothing
                return match
            }
        }));
    }

    protected showList(textRange: ITextRange, textBounds: ClientRect) {
        const editorBounds = this.editor.getContentAreaContainer().getBoundingClientRect();
        const viewportBounds = document.documentElement.getBoundingClientRect();

        const sl = document.documentElement.scrollLeft;
        const st = document.documentElement.scrollTop;

        const l = editorBounds.left + textBounds.left + sl;
        const t = editorBounds.top + textBounds.top + st;

        this.highlight.rect({
            left: l + 'px',
            top: t + 'px',
            width: textBounds.width + 'px',
            height: textBounds.height + 'px',
            bottom: '',
            right: '',
        });

        const rect: IRectCss = {
            top: '',
            left: '',
            right: '',
            bottom: '',
            width: '500px',
            height: 'auto',
        };

        if (l <= viewportBounds.width / 2 + sl) {
            const left = editorBounds.left + textBounds.left + sl;
            rect.left = Math.max(left, sl) + 'px';
        } else {
            const r = -editorBounds.left + viewportBounds.right - textBounds.right;
            rect.right = Math.max(r, -sl) + 'px';
        }
        if (t <= viewportBounds.height / 2 + st) {
            const top = editorBounds.top + textBounds.top + st + textBounds.height;
            rect.top = Math.max(top, st) + 'px';
        } else {
            const b = -editorBounds.top + viewportBounds.bottom - textBounds.bottom + textBounds.height;
            rect.bottom = Math.max(b, -st) + 'px';
        }

        this.list.show({textRange: textRange, styles: rect});
    }

    protected hasFocus(): boolean {
        return this.editor.getWin()?.frameElement === document.activeElement;
    }

    protected override getEl(): HTMLTextAreaElement {
        return super.getEl() as HTMLTextAreaElement;
    }

    public getTextRange(): ITextRange {
        const range = this.editor.selection.getRng();
        return getTextRange(range.startOffset, range.commonAncestorContainer.textContent ?? '') as ITextRange;
    }

    protected getBoundsAndRange(): IBoundsAndRange | null {
        let selection: Selection | null;
        let selectionRange: Range;
        try {
            this.editor.getBody().normalize();
            selection = this.getDocument().getSelection();
            if (!selection || !selection.rangeCount) {
                return null;
            }
            selectionRange = selection.getRangeAt(0).cloneRange();
        } catch (err) {
            return null;
        }

        if (selectionRange.startContainer !== selectionRange.endContainer) {
            return null;
        }

        const textRange = getTextRange(selectionRange.startOffset, selectionRange.commonAncestorContainer.textContent ?? '');

        if (!textRange) {
            return null;
        }

        try {
            selectionRange.setStart(selectionRange.commonAncestorContainer, textRange.start - 1);
            selectionRange.setEnd(selectionRange.commonAncestorContainer, textRange.end + 1);
        } catch (err) {
            return null;
        }

        return {
            selectionRange: selectionRange,
            textRange: textRange,
            textBounds: selectionRange.getBoundingClientRect(),
        }
    }

    protected insertVariable(syncfield: ISyncField) {
        const variableNameObject = this.getVariableNameObject(syncfield);
        const obj = this.getBoundsAndRange();
        if (!obj) {
            return;
        }
        const {textRange} = obj;
        const sIdx = textRange.start;
        const eIdx = textRange.end;
        let displayName: string;
        switch (syncfield.objecttypeid) {
            case ObjectTypeId.Function:
            case ObjectTypeId.FormFunction:
            case ObjectTypeId.FunctionAlias:
                displayName = variableNameObject.displayname ?? syncfield.name;
                break
            default:
                displayName = syncfield.name;
        }
        this.replaceSelectedText(sIdx, eIdx, displayName);

        if (variableNameObject.isFunction && variableNameObject.delimiterIndexes.length) {
            const selection = this.getDocument().getSelection();
            if (!selection) {
                return;
            }
            const node = selection.anchorNode;
            if (!node) {
                return;
            }
            const range = this.getDocument().createRange();
            const firstDelimiter = variableNameObject.delimiterIndexes[0];
            if (firstDelimiter === undefined) {
                return;
            }
            const eDelimiter = variableNameObject.delimiterIndexes[1] ? variableNameObject.delimiterIndexes[1] - 1 : displayName.length;
            range.setStart(node, sIdx + firstDelimiter);
            range.setEnd(node, sIdx + eDelimiter);
            selection.removeAllRanges();
            selection.addRange(range);
        } else {
            this.setCaretPosition(sIdx + displayName.length + 1);
        }

        this.onEvent();
    }

    protected insertParameter(syncfield: ISyncField, value: string, index: number): void {
        this.editor.execCommand('mceFocus', false, this.id);
        const bounds = this.getBoundsAndRange();
        if (!bounds) {
            return;
        }
        const {textRange} = bounds;
        const text = this.editor.selection.getRng().commonAncestorContainer.textContent ?? '';
        const indices: number[] = [];
        for (let i = textRange.start; i < textRange.end; i++) {
            if (text[i] === ";") indices.push(i);
        }
        const prevIndex = indices[index - 1];
        if (prevIndex === undefined) {
            return;
        }
        const sIdx = prevIndex + 1;
        const eIdx = indices[index] ?? textRange.end;
        this.replaceSelectedText(sIdx, eIdx, value);
        this.setCaretPosition(sIdx + value.length + 1);
        this.onEvent();
    }

    public insertString(str: string) {
        const bounds = this.getBoundsAndRange();
        if (!bounds) {
            return;
        }
        const {textRange} = bounds;
        this.replaceSelectedText(textRange.start, textRange.end, str);
        this.setCaretPosition(textRange.start + str.length);
        this.updateParamsExpression();
    }

    /** Replaces a character range via the editor undo stack (Ctrl+Z / Ctrl+Y). */
    private replaceSelectedText(start: number, end: number, text: string): void {
        this.editor.focus();
        this.editor.undoManager.transact(() => {
            const node = this.editor.selection.getRng().commonAncestorContainer;
            const range = this.getDocument().createRange();
            range.setStart(node, start);
            range.setEnd(node, end);
            this.editor.selection.setRng(range);
            this.editor.insertContent(text);
        });
    }

    private insertRawString(str: string) {
        this.editor.undoManager.transact(() => {
            this.editor.insertContent(str);
        });
    }

    protected getDocument(): Document {
        return this.editor.getDoc();
    }

    protected setCaretPosition(position: number) {
        const selection = this.getDocument().getSelection();
        if (!selection) {
            return;
        }
        const node = selection.anchorNode;
        if (!node) {
            return;
        }
        const range = this.getDocument().createRange();
        range.setStart(node, position);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    addEventListener(event: string, handler: () => void): void {
        this.editor.on(event, handler);
    }

    removeEventListener(event: string, handler: () => void): void {
        this.editor.off(event, handler);
    }

    protected override updateShadowStyle() {
        this.shadow.style.whiteSpace = "pre-wrap";
    }

    override dispose() {
        super.dispose();
        this.killListeners();
        this.editor?.remove();
        this.editor?.destroy();
        global.highlightManager.remove(this.id);
        global.listManager.remove(this.id);
    }

}
